/**
 * A selectable category facet for the Flying Club Finder. `value` is the full tag name the API returns
 * on FlyingClubResult.tags (so client-side filtering matches against it); `label` is the UI text.
 */
export interface FlyingClubFacetOption {
  label: string;
  value: string;
}

/**
 * Membership facets — OR logic (a club matches if it carries ANY selected tag), mirroring the iOS app.
 */
export const FLYING_CLUB_MEMBERSHIP_OPTIONS: FlyingClubFacetOption[] = [
  { label: 'Accepting New Members', value: 'Flying Club::Membership::Open' },
  { label: 'Waiting List', value: 'Flying Club::Membership::Waiting List' }
];

/**
 * Benefits / facilities facets — AND logic (a club must carry ALL selected tags).
 */
export const FLYING_CLUB_BENEFITS_OPTIONS: FlyingClubFacetOption[] = [
  { label: 'Clubhouse', value: 'Flying Club::Benefits::Clubhouse' },
  { label: 'Flight Instructor Available', value: 'Flying Club::Benefits::Flight Instructor Available' },
  { label: 'Kitchen/Food', value: 'Flying Club::Benefits::Kitchen/Food' },
  { label: 'Online Flight Scheduler', value: 'Flying Club::Benefits::Online Flight Scheduler' },
  { label: 'Restrooms', value: 'Flying Club::Benefits::Restrooms' },
  { label: 'Simulator', value: 'Flying Club::Benefits::Simulator' },
  { label: 'Social Programs', value: 'Flying Club::Benefits::Social Programs' }
];

/**
 * Types of Flying facets — AND logic.
 */
export const FLYING_CLUB_FLYING_OPTIONS: FlyingClubFacetOption[] = [
  { label: 'Aerobatic', value: 'Flying Club::Flying::Aerobatic' },
  { label: 'Complex', value: 'Flying Club::Flying::Complex' },
  { label: 'Cross Country', value: 'Flying Club::Flying::Cross Country' },
  { label: 'Floats', value: 'Flying Club::Flying::Floats' },
  { label: 'Glass Panel', value: 'Flying Club::Flying::Glass Panel' },
  { label: 'Glider', value: 'Flying Club::Flying::Glider' },
  { label: 'High Performance', value: 'Flying Club::Flying::High Performance' },
  { label: 'Hot Air Balloon', value: 'Flying Club::Flying::Hot Air Balloon' },
  { label: 'IFR', value: 'Flying Club::Flying::IFR' },
  { label: 'Light Sport', value: 'Flying Club::Flying::Light Sport' },
  { label: 'Local', value: 'Flying Club::Flying::Local' },
  { label: 'Multi', value: 'Flying Club::Flying::Multi' },
  { label: 'Rotorcraft', value: 'Flying Club::Flying::Rotorcraft' },
  { label: 'Skis', value: 'Flying Club::Flying::Skis' },
  { label: 'Tailwheel', value: 'Flying Club::Flying::Tailwheel' },
  { label: 'Ultralight', value: 'Flying Club::Flying::Ultralight' },
  { label: 'VFR', value: 'Flying Club::Flying::VFR' },
  { label: 'Warbirds', value: 'Flying Club::Flying::Warbirds' }
];

/**
 * Tag name marking an equity club.
 */
export const FLYING_CLUB_EQUITY_TAG = 'Flying Club::Equity Club';

/**
 * Tag name marking a non-equity club.
 */
export const FLYING_CLUB_NON_EQUITY_TAG = 'Flying Club::Non-Equity Club';

/**
 * Tag name marking a hybrid-equity club. Hybrid-Equity clubs match whenever the Equity or
 * Non-Equity filter is selected (see SearchPageComponent.filterFlyingClubs), so this tag is not
 * exposed as its own filter checkbox.
 */
export const FLYING_CLUB_HYBRID_EQUITY_TAG = 'Flying Club::Hybrid-Equity Club';

/**
 * Filter state for the Flying Club Finder. Holds the keyword plus the selected category facets.
 * Selected facets are stored as arrays of tag names so the OR/AND filtering logic (see
 * SearchPageComponent.filterFlyingClubs) can match directly against FlyingClubResult.tags.
 * Empty arrays / false booleans mean "no constraint" (show all). Mirrors the plain-class convention
 * of AviationEventFilter.
 */
export class FlyingClubFinderFilter {

  /** Keyword: club name or airport ident. */
  name = '';

  /** Selected Membership tag names (OR logic). */
  membership: string[] = [];

  /** Selected Benefits/facilities tag names (AND logic). */
  benefits: string[] = [];

  /** Selected Types of Flying tag names (AND logic). */
  flying: string[] = [];

  /** Limit to AOPA Network clubs (matched on clubType). */
  aopaNetwork = false;

  /** Limit to clubs in formation (matched on clubType). */
  inFormation = false;

  /** Limit to equity clubs (matched on the Equity Club tag; also matches Hybrid-Equity clubs). */
  equityClub = false;

  /** Limit to non-equity clubs (matched on the Non-Equity Club tag; also matches Hybrid-Equity clubs). */
  nonEquityClub = false;
}
